import { isMock } from 'lib/util/isMock'
import { setupWorker, SetupWorkerApi } from 'msw'
import { GatewayHandler } from '../../stories/HrznGatewayMswBuilder/gateway_handler'
import { HrznGatewayMswBuilder } from '../../stories/HrznGatewayMswBuilder/HrznGatewayMswBuilder'

declare global {
  interface Window {
    /**
     * @classdesc This is just a class used to contain the mock service worker so we can access it anywhere in the application
     *
     * This class will instantiate itself and attach itself to the window object on first import
     *
     * Add an import to your entry file like so:
     * ``` typescript
     * import 'PATH_TO_MSW_MANAGER'
     * ```
     *
     * Then use the managers available functions by accessing it via the window object
     * ``` typescript
     * window.MswManager.CallFunction()
     * ```
     *
     * @example ``` typescript
     * // You can use this when creating a new react query hook
     * const useSomeQueryName = () => {
     * // do some stuff
     * useMswManager(useSomeQueryNameBaseMswHandler) // this will only ever work in dev since MswManager will not be available in other envs
     * }
     * ```
     * @author Triston Armstrong
     * @access Dev Mode Only
     */
    MswManager: MSWManager | undefined
  }
}
class MSWManager {
  private _worker: SetupWorkerApi

  constructor() {
    this._worker = setupWorker(HrznGatewayMswBuilder.create().build())
  }

  static init() {
    const MswManager = new this()
    if (isMock()) {
      window.MswManager = MswManager
      window.MswManager.run()
    } // silently do nothing
    return MswManager
  }

  run() {
    void this._worker.start()
  }

  stop() {
    this._worker.stop()
  }

  /**
   * Calls .use() on the worker instance to add the handlers to it
   * @param handler GatewayHandler
   * @note Dont forget to call .build() on your HrznGatewayMswBuilder storyUtil functions
   */
  assignHandler<T>(handler: CheckIsGatewayHandler<T>) {
    this._worker.use(handler as GatewayHandler)
  }
}

export default MSWManager.init()

type CheckIsGatewayHandler<Arg> = Arg extends GatewayHandler
  ? Arg
  : 'You may have forgotten to call build on your MswBuilder'
